import * as React from "react";
import { useMemo, useState } from "react";
import styled from "styled-components";
import upperFirst from "lodash/upperFirst";
import toNumber from "lodash/toNumber";
import toString from "lodash/toString";
import { useMantineTheme } from "@mantine/core";
import { useTranslation } from "react-i18next";
import useMediaQuery from "util/hooks/useMediaQuery";
import { Select } from "components/common";
import { MarkdownEditor, MarkdownPreview } from "components/common/MarkdownEditor";
import { Button, Col, ControlLabel, FormGroup, HelpBlock, Row, Input } from "components/bootstrap";
import EventDefinitionPriorityEnum from "logic/alerts/EventDefinitionPriorityEnum";
import usePluginEntities from "hooks/usePluginEntities";
import usePluggableLicenseCheck from "hooks/usePluggableLicenseCheck";
import * as FormsUtils from "util/FormsUtils";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import EventSummaryTemplateHelp from "./EventSummaryTemplateHelp";
import { isSystemEventDefinition } from "../event-definitions-types";
import commonStyles from "../common/commonStyles.css";
const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacings.lg};

  & .form-group {
    width: 100%;
  }
`;
const InputContainer = styled.div`
  display: inline-block;
  position: relative;
  width: 100%;
`;
const InputFeedback = styled.div`
  position: absolute;
  right: 0;
  top: 24px;
  display: flex;
  align-items: center;
  min-height: 34px;
  padding-right: 3px;
`;
const priorities = Object.entries(EventDefinitionPriorityEnum.properties).map(([key, value]) => ({
  value: key,
  name: value.name
})).sort((a, b) => Number(b.value) - Number(a.value));
const EventDetailsForm = ({ eventDefinition, eventDefinitionEventProcedure, validation, onChange, canEdit }) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  const { t } = useTranslation("event_definitions");
  const priorityOptions = priorities.map(({ value, name }) => ({ value, label: t(`priorities.${name}`) }));
  const theme = useMantineTheme();
  const ltXl = useMediaQuery(`(min-width: ${theme.breakpoints.xl}`);
  const sendTelemetry = useSendTelemetry("event-definition-details");
  const [showAddEventProcedureForm, setShowAddEventProcedureForm] = useState(false);
  const {
    data: { valid: validSecurityLicense }
  } = usePluggableLicenseCheck("/license/security");
  const readOnly = useMemo(() => !canEdit || isSystemEventDefinition(eventDefinition), [canEdit, eventDefinition]);
  const showEventProcedureSummary = useMemo(
    () => !!eventDefinitionEventProcedure && !showAddEventProcedureForm && validSecurityLicense,
    [eventDefinitionEventProcedure, showAddEventProcedureForm, validSecurityLicense]
  );
  const showAddNewEventProcedure = useMemo(
    () => !eventDefinitionEventProcedure && !showAddEventProcedureForm && !readOnly && validSecurityLicense,
    [eventDefinitionEventProcedure, showAddEventProcedureForm, readOnly, validSecurityLicense]
  );
  const handleChange = (event) => {
    const { name } = event.target;
    onChange(name, String(FormsUtils.getValueFromInput(event.target)));
  };
  const handlePriorityChange = (nextPriority) => {
    var _a2;
    sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_DETAILS.PRIORITY_CHANGED, {
      app_action_value: "priority-select",
      priority: upperFirst((_a2 = priorities.find((opt) => opt.value === nextPriority)) == null ? void 0 : _a2.name)
    });
    onChange("priority", toNumber(nextPriority));
  };
  const PluggableEventProcedureForm = (_b = (_a = usePluginEntities("views.components.eventProcedureForm")) == null ? void 0 : _a[0]) == null ? void 0 : _b.component;
  const PluggableEventProcedureSummary = (_d = (_c = usePluginEntities("views.components.eventProcedureSummary")) == null ? void 0 : _c[0]) == null ? void 0 : _d.component;
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12, lg: 6 }, /* @__PURE__ */ React.createElement("h2", { className: commonStyles.title }, t("details_form.title")), /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement(StyledRow, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "event-definition-title",
      name: "title",
      label: t("details_form.title_label"),
      type: "text",
      bsStyle: validation.errors.title ? "error" : null,
      help: (_g = (_f = (_e = validation == null ? void 0 : validation.errors) == null ? void 0 : _e.title) == null ? void 0 : _f[0]) != null ? _g : t("details_form.title_help"),
      value: eventDefinition.title,
      onChange: handleChange,
      readOnly,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "event-definition-priority" }, /* @__PURE__ */ React.createElement(ControlLabel, null, t("details_form.priority")), /* @__PURE__ */ React.createElement(
    Select,
    {
      options: priorityOptions,
      value: toString(eventDefinition.priority),
      onChange: handlePriorityChange,
      clearable: false,
      disabled: readOnly,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, t("details_form.priority_help")))), /* @__PURE__ */ React.createElement(InputContainer, { className: "input-container" }, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "event-definition-event-summary-template",
      name: "event_summary_template",
      label: t("details_form.summary_template"),
      type: "text",
      help: t("details_form.summary_template_help"),
      value: eventDefinition.event_summary_template,
      onChange: handleChange,
      readOnly
    }
  ), /* @__PURE__ */ React.createElement(InputFeedback, null, /* @__PURE__ */ React.createElement(EventSummaryTemplateHelp, null))), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "event-definition-description",
      name: "description",
      label: /* @__PURE__ */ React.createElement("span", null, t("details_form.description"), " ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, t("filter_form.optional"))),
      type: "textarea",
      help: t("details_form.description_help"),
      value: eventDefinition.description,
      onChange: handleChange,
      readOnly,
      rows: 2
    }
  ), !validSecurityLicense && /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, t("details_form.remediation_steps"), " ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, t("details_form.optional"))), readOnly ? /* @__PURE__ */ React.createElement(
    MarkdownPreview,
    {
      show: true,
      withFullView: true,
      height: 150,
      value: eventDefinition.remediation_steps || t("details_form.no_remediation_steps")
    }
  ) : /* @__PURE__ */ React.createElement(
    MarkdownEditor,
    {
      id: "event-definition-remediation-steps",
      height: 150,
      value: (_h = eventDefinition.remediation_steps) != null ? _h : "",
      onChange: (newValue) => onChange("remediation_steps", newValue)
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, t("details_form.remediation_steps_help"))), validSecurityLicense && showAddEventProcedureForm && /* @__PURE__ */ React.createElement(
    PluggableEventProcedureForm,
    {
      eventProcedureId: eventDefinitionEventProcedure,
      remediationSteps: eventDefinition == null ? void 0 : eventDefinition.remediation_steps,
      onClose: () => setShowAddEventProcedureForm(false),
      onSave: (eventProcedureId) => {
        onChange("event_procedure", eventProcedureId);
        setShowAddEventProcedureForm(false);
      },
      onRemove: () => {
        onChange("event_procedure", null);
        setShowAddEventProcedureForm(false);
      }
    }
  ), showEventProcedureSummary && /* @__PURE__ */ React.createElement(Col, null, /* @__PURE__ */ React.createElement(ControlLabel, null, t("summary_renders.event_procedure_summary")), /* @__PURE__ */ React.createElement(
    PluggableEventProcedureSummary,
    {
      eventProcedureId: eventDefinitionEventProcedure,
      canEdit: !readOnly,
      onEdit: () => setShowAddEventProcedureForm(true),
      onRemove: () => onChange("event_procedure", null),
      row: ltXl
    }
  )), showAddNewEventProcedure && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ControlLabel, null, t("summary_renders.event_procedure_summary")), /* @__PURE__ */ React.createElement("p", null, t("details_form.no_event_procedures")), /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", onClick: () => setShowAddEventProcedureForm(true) }, t("details_form.add_event_procedure"))))));
};
export default EventDetailsForm;
